import { useCallback, useLayoutEffect, useState } from 'react';

interface UseVisibleAndOverflowedButtonsResult {
  visibleElementsCount: number;
}

interface UseVisibleAndOverflowedButtonsParams {
  childCount: number;
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export function useVisibleAndOverflowedButtons({
  childCount,
  containerRef,
}: UseVisibleAndOverflowedButtonsParams): UseVisibleAndOverflowedButtonsResult {
  const [visibleElementsCount, setVisibleElementsCount] = useState(childCount);

  const updateVisibleElementsCount = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const overflowWrapper = container.querySelector<HTMLElement>('[data-toolbar-overflow-wrapper]');
    const toolbarElements = Array.from(
      container.querySelectorAll<HTMLElement>('[data-toolbar-item]')
    );

    if (!toolbarElements.length || !overflowWrapper) {
      setVisibleElementsCount(0);
      return;
    }

    const containerWidth = container.getBoundingClientRect().width;
    const overflowWrapperWidth = overflowWrapper.getBoundingClientRect().width;

    function determineVisibleCount(availableWidth: number) {
      if (availableWidth <= 0) return 0;

      let accWidth = 0;
      let count = 0;

      for (const element of toolbarElements) {
        const elementWidth = element.getBoundingClientRect().width;

        if (accWidth + elementWidth > availableWidth) {
          break;
        }

        accWidth += elementWidth;
        count++;
      }

      return count;
    }

    let nextVisibleCount = determineVisibleCount(containerWidth);

    // If some children must be hidden, ensure there is room for the overflow wrapper
    if (nextVisibleCount < childCount) {
      nextVisibleCount = determineVisibleCount(containerWidth - overflowWrapperWidth);
    }

    setVisibleElementsCount(Math.max(0, Math.min(nextVisibleCount, childCount)));
  }, [childCount, containerRef]);

  useLayoutEffect(() => {
    updateVisibleElementsCount();

    const container = containerRef.current;
    if (!container) return;

    const observer = new ResizeObserver(updateVisibleElementsCount);
    observer.observe(container);
    return () => observer.disconnect();
  }, [updateVisibleElementsCount, containerRef]);

  return {
    visibleElementsCount,
  };
}
